import React, { useEffect, useState } from "react";
import Segmented from "../ui/Segmented";
import IconButton from "../ui/IconButton";
import Icon from "../ui/Icon";
import useTheme from "../../hooks/useTheme";
import "./ThemeSwitch.scss";

const OPTIONS = [
  { value: "dark", label: "Dark", icon: <Icon name="Moon" size="sm" /> },
  { value: "light", label: "Light", icon: <Icon name="Sun" size="sm" /> },
];

// Matches rc-ui.scss's `mobile` mixin ($bp-md - 1px, styles/_breakpoints.scss) — the exact
// cutoff where AppShell swaps Topbar's own chrome for MobileTopbar's sticky row. The desktop
// pill has to stop rendering (not just hide visually) below this width: rc-ui.scss reveals
// `.rc-ui-topbar__nav-strip` whenever `.rc-ui-topbar__right` holds a child that isn't the
// profile icon button or the bell (`:has(...)`), so leaving this control in the DOM — even
// display:none — would phantom-match that selector and pop an empty row under every page
// title on mobile. MobileTopbar renders its own `compact` toggle instead; see Topbar.jsx /
// MobileTopbar.jsx.
const MOBILE_CHROME_QUERY = "(max-width: 767px)";

function useIsMobileChrome() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(MOBILE_CHROME_QUERY).matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(MOBILE_CHROME_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

/**
 * ThemeSwitch — the real dark/light control, wired to `useTheme()` (Fontaine).
 *
 * Reads `resolved` (never the raw `theme`): a user on "system" sees today's
 * actual look highlighted, not a meaningless third pill state. Clicking a
 * side always calls `setTheme("dark"|"light")` explicitly — the control
 * never writes "system" back, matching the 2-option pill (no system option
 * shown at all).
 *
 * `compact` swaps the labeled Segmented pill for a single icon toggle — the
 * mobile topbar is already carrying a menu button + bell, and a full pill
 * (even icon-only) reads as one control too many there. The compact button
 * shows the CURRENT resolved theme's icon and its aria-label states the
 * action ("Switch to light theme"), so a screen-reader user hears what
 * tapping does rather than just the current state.
 *
 * @param {"sm"|"md"} [size="md"]
 * @param {boolean} [compact=false]  icon-only toggle button instead of the labeled pill
 * @param {string} [className]
 */
export default function ThemeSwitch({ size = "md", compact = false, className = "" }) {
  const { resolved, setTheme } = useTheme();
  const isMobileChrome = useIsMobileChrome();

  if (compact) {
    const next = resolved === "dark" ? "light" : "dark";
    return (
      <IconButton
        size={size}
        variant="ghost"
        aria-label={`Switch to ${next} theme`}
        className={["rc-theme-switch rc-theme-switch--compact", className].filter(Boolean).join(" ")}
        onClick={() => setTheme(next)}
      >
        <Icon name={resolved === "dark" ? "Moon" : "Sun"} size="sm" />
      </IconButton>
    );
  }

  // Desktop-only pill — see MOBILE_CHROME_QUERY above for why this must not render at all
  // (not just hide) below the mobile-chrome cutoff.
  if (isMobileChrome) return null;

  return (
    <Segmented
      aria-label="Theme"
      size={size}
      className={["rc-theme-switch", className].filter(Boolean).join(" ")}
      options={OPTIONS}
      value={resolved}
      onChange={(next) => setTheme(next)}
    />
  );
}
